import type {
  SelectedThreadUnreadSummary,
  ThreadCapabilities,
  ThreadDetail,
  ThreadFollowState,
  ThreadListItem,
  ThreadListView,
  ThreadPage,
  ThreadPageParam,
  ThreadUnreadSummary,
  ThreadUnreadTarget,
} from "@/features/threads/types";
import { api } from "@/lib/axios";

const object = (value: unknown, label: string): Record<string, unknown> => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`Malformed ${label} response`);
  }
  return value as Record<string, unknown>;
};
const number = (value: unknown, label: string) => {
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new Error(`Malformed ${label} response`);
  return value;
};
const nonnegative = (value: unknown, label: string) => {
  const parsed = number(value, label);
  if (parsed < 0) throw new Error(`Malformed ${label} response`);
  return parsed;
};
const string = (value: unknown, label: string) => {
  if (typeof value !== "string") throw new Error(`Malformed ${label} response`);
  return value;
};
const boolean = (value: unknown, label: string) => {
  if (typeof value !== "boolean")
    throw new Error(`Malformed ${label} response`);
  return value;
};
const nullable = <T>(value: unknown, parse: (value: unknown) => T) =>
  value === null ? null : parse(value);
const optional = <T>(value: unknown, parse: (value: unknown) => T) =>
  value === undefined ? undefined : parse(value);

const user = (value: unknown, label: string) => {
  const record = object(value, label);
  number(record.id, `${label} id`);
  string(record.guid, `${label} guid`);
  string(record.display_name, `${label} display name`);
  string(record.url, `${label} url`);
  string(record.image_url, `${label} image url`);
  optional(record.is_blocked_by_current_user, (v) =>
    boolean(v, `${label} blocked`),
  );
  optional(record.profile, (v) => {
    const profile = object(v, `${label} profile`);
    optional(profile.firstname, (item) =>
      string(item, `${label} profile first name`),
    );
    optional(profile.lastname, (item) =>
      string(item, `${label} profile last name`),
    );
    return profile;
  });
  return record;
};

const file = (value: unknown, label: string) => {
  const record = object(value, label);
  number(record.id, `${label} id`);
  string(record.guid, `${label} guid`);
  string(record.mime_type, `${label} mime type`);
  if (typeof record.size !== "number" && typeof record.size !== "string") {
    throw new Error(`Malformed ${label} size response`);
  }
  string(record.file_name, `${label} name`);
  string(record.url, `${label} url`);
  optional(record.thumbnail_url, (v) => string(v, `${label} thumbnail url`));
  optional(record.preview_url, (v) =>
    nullable(v, (item) => string(item, `${label} preview url`)),
  );
  optional(record.download_url, (v) =>
    nullable(v, (item) => string(item, `${label} download url`)),
  );
  optional(record.access_url_expires_at, (v) =>
    nullable(v, (item) => number(item, `${label} expiry`)),
  );
};

const reaction = (value: unknown, label: string) => {
  const record = object(value, label);
  optional(record.total, (v) => nonnegative(v, `${label} total`));
  if (
    record.current_user_emotion !== undefined &&
    record.current_user_emotion !== null &&
    typeof record.current_user_emotion !== "string"
  ) {
    throw new Error(`Malformed ${label} current emotion response`);
  }
  optional(record.last_reactions, (v) => {
    if (!Array.isArray(v)) throw new Error(`Malformed ${label} items response`);
    v.forEach((item) => {
      const entry = object(item, `${label} item`);
      number(entry.id, `${label} item id`);
      number(entry.created_by, `${label} creator id`);
      string(entry.emotion, `${label} emotion`);
      string(entry.created_at, `${label} created at`);
    });
    return v;
  });
};

const files = (value: unknown, label: string) => {
  if (!Array.isArray(value)) throw new Error(`Malformed ${label} response`);
  value.forEach((item) => {
    file(item, `${label} item`);
  });
};

const capabilities = (value: unknown): ThreadCapabilities => {
  const record = object(value, "thread capabilities");
  return {
    can_follow: boolean(record.can_follow, "can_follow"),
    can_unfollow: boolean(record.can_unfollow, "can_unfollow"),
    can_favorite: boolean(record.can_favorite, "can_favorite"),
    can_unfavorite: boolean(record.can_unfavorite, "can_unfavorite"),
    can_comment: boolean(record.can_comment, "can_comment"),
    can_edit: boolean(record.can_edit, "can_edit"),
    can_delete: boolean(record.can_delete, "can_delete"),
    can_move: boolean(record.can_move, "can_move"),
  };
};

const listItem = (value: unknown): ThreadListItem => {
  const row = object(value, "thread row");
  const author =
    row.author === null ? null : object(row.author, "thread author");
  if (author) {
    number(author.id, "thread author id");
    string(author.display_name, "thread author name");
    string(author.image_url, "thread author image");
  }
  return {
    ...(row as unknown as ThreadListItem),
    root_post_id: number(row.root_post_id, "root post id"),
    root_post_content_id: number(
      row.root_post_content_id,
      "root post content id",
    ),
    group_id: nullable(row.group_id, (v) => number(v, "group id")),
    group_name: nullable(row.group_name, (v) => string(v, "group name")),
    channel_id: nullable(row.channel_id, (v) => number(v, "channel id")),
    channel_name: nullable(row.channel_name, (v) => string(v, "channel name")),
    author: author as ThreadListItem["author"],
    snippet: string(row.snippet, "snippet"),
    created_at: string(row.created_at, "created at"),
    last_activity_at: nullable(row.last_activity_at, (v) =>
      string(v, "last activity at"),
    ),
    is_following: boolean(row.is_following, "is following"),
    notification_enabled: boolean(
      row.notification_enabled,
      "notification enabled",
    ),
    is_favorite: boolean(row.is_favorite, "is favorite"),
    unread_ordinary_count: nonnegative(
      row.unread_ordinary_count,
      "unread count",
    ),
    has_unread_ordinary: boolean(row.has_unread_ordinary, "has unread"),
    unread_mention_count: nonnegative(
      row.unread_mention_count,
      "mention count",
    ),
    capabilities: capabilities(row.capabilities),
  };
};

const page = (value: unknown): ThreadPage => {
  const record = object(value, "thread page");
  if (!Array.isArray(record.results))
    throw new Error("Malformed thread page response");
  return {
    results: record.results.map(listItem),
    next_cursor: nullable(record.next_cursor, (v) => string(v, "next cursor")),
    version: string(record.version, "version"),
    snapshot_version: string(record.snapshot_version, "snapshot version"),
    complete: boolean(record.complete, "complete"),
    restart_required: boolean(record.restart_required, "restart required"),
  };
};

export const normalizeThreadUnreadTarget = (
  value: unknown,
  rootPostContentId: number,
): ThreadUnreadTarget => {
  const target = object(value, "unread target");
  const activityType = string(target.activity_type, "activity type");
  if (!["post", "comment", "reply"].includes(activityType))
    throw new Error("Malformed activity type response");
  return {
    stream_activity_id: number(target.stream_activity_id, "stream activity id"),
    activity_type: activityType as ThreadUnreadTarget["activity_type"],
    activity_id: number(target.activity_id, "activity id"),
    post_id:
      typeof target.post_id === "number"
        ? number(target.post_id, "post id")
        : null,
    post_content_id:
      target.post_content_id === null || target.post_content_id === undefined
        ? rootPostContentId
        : number(target.post_content_id, "post content id"),
    parent_comment_id: nullable(target.parent_comment_id, (v) =>
      number(v, "parent comment id"),
    ),
    created_at: string(target.created_at, "target created at"),
    is_mention: boolean(target.is_mention, "is mention"),
  };
};

const comment = (value: unknown): void => {
  const record = object(value, "thread comment");
  number(record.id, "comment id");
  string(record.message, "comment message");
  optional(record.message_original, (v) =>
    string(v, "original comment message"),
  );
  optional(record.objectModel, (v) => string(v, "comment object model"));
  optional(record.object_model, (v) => string(v, "comment object model"));
  optional(record.object_id, (v) => number(v, "comment object id"));
  optional(record.createdBy, (v) => user(v, "comment author"));
  optional(record.user, (v) => user(v, "comment author"));
  optional(record.createdAt, (v) => string(v, "comment created at"));
  optional(record.created_at, (v) => string(v, "comment created at"));
  optional(record.files, (v) => files(v, "comment files"));
  optional(record.reactions, (v) => reaction(v, "comment reactions"));
  optional(record.likes, (v) => {
    const likes = object(v, "comment likes");
    nonnegative(likes.total, "comment likes total");
    return likes;
  });
  if (record.stream_activity_id !== undefined) {
    nullable(record.stream_activity_id, (v) =>
      number(v, "comment activity id"),
    );
  }
  if (record.is_unread !== undefined)
    boolean(record.is_unread, "comment unread");
  if (record.is_unread_mention !== undefined)
    boolean(record.is_unread_mention, "comment mention");
  if (record.commentsCount !== undefined)
    nonnegative(record.commentsCount, "comment count");
  if (record.commentsHasMore !== undefined)
    boolean(record.commentsHasMore, "comments has more");
  if (record.comments !== undefined) {
    if (!Array.isArray(record.comments))
      throw new Error("Malformed thread comments response");
    record.comments.forEach(comment);
  }
  if (record.capabilities !== undefined) {
    const caps = object(record.capabilities, "comment capabilities");
    boolean(caps.can_edit, "comment can edit");
    boolean(caps.can_delete, "comment can delete");
  }
};

const post = (value: Record<string, unknown>) => {
  number(value.id, "post id");
  string(value.message, "post message");
  optional(value.message_original, (v) => string(v, "original post message"));
  boolean(value.user_marked_readlater, "post favorite state");
  number(value.space_id, "post space id");
  string(value.space_name, "post space name");

  const content = object(value.content, "post content");
  number(content.id, "post content id");
  const metadata = object(content.metadata, "post metadata");
  number(metadata.id, "post metadata id");
  string(metadata.guid, "post guid");
  string(metadata.object_model, "post object model");
  number(metadata.object_id, "post object id");
  number(metadata.visibility, "post visibility");
  number(metadata.state, "post state");
  boolean(metadata.archived, "post archived");
  boolean(metadata.hidden, "post hidden");
  boolean(metadata.pinned, "post pinned");
  boolean(metadata.locked_comments, "post comments locked");
  user(metadata.created_by, "post author");
  string(metadata.created_at, "post created at");
  user(metadata.updated_by, "post updater");
  string(metadata.updated_at, "post updated at");
  string(metadata.url, "post url");
  number(metadata.contentcontainer_id, "post content container id");
  string(metadata.stream_channel, "post stream channel");
  const comments = object(content.comments, "post comments");
  nonnegative(comments.total, "post comments total");
  if (!Array.isArray(comments.latest)) {
    throw new Error("Malformed post latest comments response");
  }
  comments.latest.forEach(comment);
  const likes = object(content.likes, "post likes");
  nonnegative(likes.total, "post likes total");
  reaction(content.reactions, "post reactions");
  files(content.files, "post files");
  if (!Array.isArray(content.topics)) {
    throw new Error("Malformed post topics response");
  }
  content.topics.forEach((value) => {
    const topic = object(value, "post topic");
    number(topic.id, "post topic id");
    string(topic.name, "post topic name");
    optional(topic.is_archived_channel, (item) =>
      boolean(item, "post topic archived"),
    );
    optional(topic.is_admin_only_channel, (item) =>
      boolean(item, "post topic admin only"),
    );
  });
};

const followState = (value: unknown): ThreadFollowState => {
  const state = object(value, "thread follow");
  return {
    root_post_content_id: number(
      state.root_post_content_id,
      "root post content id",
    ),
    is_following: boolean(state.is_following, "is following"),
    notification_enabled: boolean(
      state.notification_enabled,
      "notification enabled",
    ),
    origin: string(state.origin, "follow origin"),
    updated_at: string(state.updated_at, "follow updated at"),
  };
};

const listPath = (view: ThreadListView) =>
  view === "favorites" ? "/readlater-posts/threads" : "/thread/following";

export const threadsApi = {
  getListPage: async (
    view: ThreadListView,
    params: ThreadPageParam,
    limit: number,
    signal?: AbortSignal,
  ) => {
    const response = await api.get(listPath(view), {
      signal,
      params: {
        limit,
        ...(params.cursor ? { cursor: params.cursor } : {}),
        ...(params.snapshotVersion
          ? { snapshot_version: params.snapshotVersion }
          : {}),
      },
    });
    return page(response.data);
  },
  getDetail: async (
    rootPostContentId: number,
    signal?: AbortSignal,
  ): Promise<ThreadDetail> => {
    const response = await api.get(`/thread/${rootPostContentId}`, { signal });
    const detail = object(response.data, "thread detail");
    if (
      !Array.isArray(detail.comments) ||
      !Array.isArray(detail.unread_targets)
    )
      throw new Error("Malformed thread detail response");
    if (
      number(detail.root_post_content_id, "root post content id") !==
      rootPostContentId
    )
      throw new Error("Malformed thread detail response");
    post(detail);
    boolean(detail.is_following, "is following");
    boolean(detail.notification_enabled, "notification enabled");
    nonnegative(detail.initial_comment_count, "initial comment count");
    nonnegative(detail.initial_reply_count, "initial reply count");
    nullable(detail.group_id, (v) => number(v, "group id"));
    nullable(detail.group_name, (v) => string(v, "group name"));
    nullable(detail.channel_id, (v) => number(v, "channel id"));
    nullable(detail.channel_name, (v) => string(v, "channel name"));
    nullable(detail.last_activity_at, (v) => string(v, "last activity at"));
    nullable(detail.root_stream_activity_id, (v) =>
      number(v, "root activity id"),
    );
    boolean(detail.comments_has_more, "comments has more");
    boolean(detail.replies_has_more, "replies has more");
    nonnegative(detail.unread_targets_total, "unread targets total");
    boolean(detail.unread_targets_has_more, "unread targets has more");
    detail.comments.forEach(comment);
    detail.unread_targets = detail.unread_targets.map((target) =>
      normalizeThreadUnreadTarget(target, rootPostContentId),
    );
    capabilities(detail.capabilities);
    return detail as unknown as ThreadDetail;
  },
  getUnreadSummary: async (
    signal?: AbortSignal,
  ): Promise<ThreadUnreadSummary> => {
    const response = await api.get("/thread/following/unread-summary", {
      signal,
    });
    const summary = object(response.data, "thread unread summary");
    return {
      unread_post_comment_count: nonnegative(
        summary.unread_post_comment_count,
        "unread count",
      ),
      has_unread_post_comments: boolean(
        summary.has_unread_post_comments,
        "has unread",
      ),
      unread_mention_count: nonnegative(
        summary.unread_mention_count,
        "mention count",
      ),
      version: string(summary.version, "version"),
      snapshot_version: string(summary.snapshot_version, "snapshot version"),
    };
  },
  getSelectedUnreadSummary: async (
    rootPostContentId: number,
    signal?: AbortSignal,
  ): Promise<SelectedThreadUnreadSummary> => {
    const response = await api.get(
      `/thread/${rootPostContentId}/unread-summary`,
      {
        signal,
      },
    );
    const summary = object(response.data, "selected thread unread summary");
    if (!Array.isArray(summary.targets)) {
      throw new Error("Malformed selected thread unread summary response");
    }
    const parsed = {
      root_post_content_id: number(
        summary.root_post_content_id,
        "root post content id",
      ),
      version: string(summary.version, "version"),
      last_activity_id: nonnegative(
        summary.last_activity_id,
        "last activity id",
      ),
      unread_count: nonnegative(summary.unread_count, "unread count"),
      unread_mention_count: nonnegative(
        summary.unread_mention_count,
        "mention count",
      ),
      targets: summary.targets.map((target) =>
        normalizeThreadUnreadTarget(target, rootPostContentId),
      ),
      targets_has_more: boolean(summary.targets_has_more, "targets has more"),
    };
    if (parsed.root_post_content_id !== rootPostContentId) {
      throw new Error("Malformed selected thread unread summary response");
    }
    return parsed;
  },
  follow: async (rootPostContentId: number) => {
    const state = followState(
      (await api.post(`/thread/${rootPostContentId}/follow`)).data,
    );
    if (state.root_post_content_id !== rootPostContentId || !state.is_following)
      throw new Error("Malformed thread follow response");
    return state;
  },
  unfollow: async (rootPostContentId: number) => {
    const state = followState(
      (await api.delete(`/thread/${rootPostContentId}/follow`)).data,
    );
    if (state.root_post_content_id !== rootPostContentId || state.is_following)
      throw new Error("Malformed thread unfollow response");
    return state;
  },
  setNotificationEnabled: async (
    rootPostContentId: number,
    enabled: boolean,
  ) => {
    const state = followState(
      (
        await api.patch(`/thread/${rootPostContentId}/notification`, {
          enabled,
        })
      ).data,
    );
    if (
      state.root_post_content_id !== rootPostContentId ||
      !state.is_following ||
      state.notification_enabled !== enabled
    ) {
      throw new Error("Malformed thread notification response");
    }
    return state;
  },
};
